import React, { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";

// Strict Tamil mode (Feb 2026): Banner that warns admins about entities lacking name_ta.
// Only shown when there are missing Tamil names, regardless of current language.
export default function MissingTamilBanner() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/admin/missing-tamil-names")
      .then(r => setData(r.data))
      .catch(() => setData(null));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  if (!data || !data.counts || data.counts.total === 0) return null;

  const c = data.counts;
  const title = lang === "ta"
    ? `${c.total} பதிவுகளில் தமிழ் பெயர் இல்லை`
    : `${c.total} entries are missing Tamil names`;
  const cta = lang === "ta" ? "விவரங்கள் காண்க" : "View details";

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4" data-testid="missing-tamil-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-amber-900 heading-font">{title}</div>
          <div className="text-xs text-amber-800 mt-1">
            {lang === "ta"
              ? "தமிழ் பயன்முறையில் ஆங்கில பெயர்கள் காட்டப்படும். தயவுசெய்து தமிழ் பெயர்களை சேர்க்கவும்."
              : "English names will fall back when Tamil mode is on. Please add Tamil names."}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <Pill k={lang === "ta" ? "மாவட்டங்கள்" : "Districts"} v={c.districts} />
            <Pill k={lang === "ta" ? "ஊழியர்கள்" : "Users"} v={c.users} />
            <Pill k={lang === "ta" ? "கடைகள்" : "Shops"} v={c.shops} />
            <Pill k={lang === "ta" ? "பொருட்கள்" : "Products"} v={c.products} />
          </div>
          <button
            onClick={() => setOpen(!open)}
            data-testid="missing-tamil-toggle"
            className="text-xs font-bold text-amber-900 mt-2 flex items-center gap-1 hover:underline"
          >
            {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {open ? (lang === "ta" ? "மறை" : "Hide") : cta}
          </button>
          {open && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <DetailList title={lang === "ta" ? "மாவட்டங்கள்" : "Districts"} items={data.districts} />
              <DetailList title={lang === "ta" ? "ஊழியர்கள்" : "Users"} items={data.users.map(u => ({ ...u, name: `${u.name} (${u.role})` }))} />
              <DetailList title={lang === "ta" ? "கடைகள்" : "Shops"} items={data.shops} />
              <DetailList title={lang === "ta" ? "பொருட்கள்" : "Products"} items={data.products} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Pill = ({ k, v }) => (
  <span className="px-2 py-0.5 bg-amber-100 border border-amber-200 rounded-full text-[11px] font-bold text-amber-900">
    {k}: {v}
  </span>
);

const DetailList = ({ title, items }) => (
  <div className="bg-white rounded-xl p-3 border border-amber-100">
    <div className="font-bold text-amber-900 mb-1.5">{title} ({items?.length || 0})</div>
    {!items || items.length === 0 ? (
      <div className="text-gray-400">—</div>
    ) : (
      <ul className="space-y-0.5 max-h-40 overflow-y-auto">
        {items.slice(0, 50).map((it) => (
          <li key={it.id} className="truncate text-gray-700">• {it.name || it.name_en || "—"}</li>
        ))}
        {items.length > 50 && <li className="text-gray-400">+{items.length - 50}…</li>}
      </ul>
    )}
  </div>
);
